/**
 * A CRUD list's own state — page, page size, sort, search, and its filter bar —
 * kept for the browser tab so coming back from a record reopens the list as it
 * was left (TKT-20261018-0003: "every time I filter and open a booking I start
 * again from the beginning").
 *
 * Opt-in per screen with `persistKey` on `DataTable` / `FilterBar`. Kept in
 * sessionStorage: per tab, gone when the tab closes, never sent anywhere.
 *
 * ⛔ The page number is only restored for the SAME filters and search it was
 *    saved under (`sig`). Page 4 of one filter is not page 4 of another.
 * ⛔ Everything read back is validated — a stale or edited value is dropped,
 *    never passed on to a query.
 * ⛔ A key is per tenant + property + user (`listStateKey`): a source filter is a
 *    property's own id, and a search holds guest names — neither may follow the
 *    user to another hotel, nor reach the next person signing in on the tab.
 *    Sign-out wipes them all (`clearListState`).
 * Pure — no React; storage is passed in.
 */

import type { SortingState } from '@tanstack/react-table'

/** The part of `Storage` this module uses. */
export type ListStateStorage = Pick<Storage, 'getItem' | 'setItem'>

/** Every key this feature writes starts with this (list-return links included). */
export const LIST_STATE_PREFIX = 'taskyinn:list-'
const TABLE_PREFIX = 'taskyinn:list-table:'
const FILTERS_PREFIX = 'taskyinn:list-filters:'
const MAX_PAGE_SIZE = 500

/** Who and where a list is being looked at. */
export interface ListStateScope {
  tenantId: string | null | undefined
  propertyId: string | null | undefined
  userId: string | null | undefined
}

/**
 * The scope part of a key — `tenant:property:user` — or null while any of the
 * three is not known yet (nothing may be read or written under a half key).
 */
export function listStateScopeKey(scope: ListStateScope): string | null {
  const { tenantId, propertyId, userId } = scope
  return tenantId && propertyId && userId ? `${tenantId}:${propertyId}:${userId}` : null
}

/** `<base>:<tenant>:<property>:<user>`, or null while the scope is not known yet. */
export function listStateKey(base: string, scope: ListStateScope): string | null {
  const scoped = listStateScopeKey(scope)
  return scoped ? `${base}:${scoped}` : null
}

/** Remove every saved list state and list link from `storage` (sign-out). */
export function clearListState(storage: Pick<Storage, 'length' | 'key' | 'removeItem'> | null): void {
  if (!storage) return
  try {
    const keys: string[] = []
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i)
      if (k && k.startsWith(LIST_STATE_PREFIX)) keys.push(k)
    }
    keys.forEach((k) => storage.removeItem(k))
  } catch {
    /* blocked storage holds nothing of ours */
  }
}

/** What a table keeps. `sig` = the filters + search the page number belongs to. */
export interface TableListState {
  page: number
  pageSize: number
  sorting: SortingState
  search: string
  sig: string
}

/** What a filter bar keeps. */
export interface FilterListState {
  values: Record<string, string>
  search: string
}

function readJson(storage: ListStateStorage | null, key: string): unknown {
  if (!storage) return null
  try {
    const raw = storage.getItem(key)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function writeJson(storage: ListStateStorage | null, key: string, value: unknown): void {
  if (!storage) return
  try {
    storage.setItem(key, JSON.stringify(value))
  } catch {
    /* storage full or blocked — the list just starts fresh next time */
  }
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v)
const isWholeNumber = (v: unknown, min: number, max: number): v is number =>
  typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max

function parseSorting(v: unknown): SortingState {
  if (!Array.isArray(v)) return []
  return v
    .filter((s): s is { id: string; desc: boolean } => isRecord(s) && typeof s.id === 'string' && s.id !== '' && typeof s.desc === 'boolean')
    .map((s) => ({ id: s.id, desc: s.desc }))
}

/**
 * A table's saved state, or null when there is none (or it is unusable).
 * `isSortable` drops a sort on a column the table no longer sorts by (renamed,
 * removed, or an edited value) — Hasura would reject it and break the list.
 */
export function readTableState(
  storage: ListStateStorage | null,
  key: string,
  isSortable: (id: string) => boolean = () => true,
): TableListState | null {
  const v = readJson(storage, TABLE_PREFIX + key)
  if (!isRecord(v)) return null
  if (!isWholeNumber(v.page, 0, Number.MAX_SAFE_INTEGER) || !isWholeNumber(v.pageSize, 1, MAX_PAGE_SIZE)) return null
  return {
    page: v.page,
    pageSize: v.pageSize,
    sorting: parseSorting(v.sorting).filter((sort) => isSortable(sort.id)),
    search: typeof v.search === 'string' ? v.search : '',
    sig: typeof v.sig === 'string' ? v.sig : '',
  }
}

/** Save a table's state. */
export function writeTableState(storage: ListStateStorage | null, key: string, state: TableListState): void {
  writeJson(storage, TABLE_PREFIX + key, state)
}

/** A filter bar's saved values, or null. Only string values for known filter names survive. */
export function readFilterState(storage: ListStateStorage | null, key: string, names: readonly string[]): FilterListState | null {
  const v = readJson(storage, FILTERS_PREFIX + key)
  if (!isRecord(v)) return null
  const values: Record<string, string> = {}
  if (isRecord(v.values)) {
    for (const name of names) {
      const value = v.values[name]
      if (typeof value === 'string' && value !== '') values[name] = value
    }
  }
  return { values, search: typeof v.search === 'string' ? v.search : '' }
}

/** Save a filter bar's values. */
export function writeFilterState(storage: ListStateStorage | null, key: string, state: FilterListState): void {
  writeJson(storage, FILTERS_PREFIX + key, state)
}

/** The identity of "which rows": the caller's filter and the search in force. */
export function tableStateSig(whereKey: string, search: string): string {
  return `${whereKey}|${search}`
}

/** The tab's sessionStorage, or null on the server / when the browser blocks it. */
export function browserListStateStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage
  } catch {
    return null
  }
}
